
import { useCallback } from "react";
import { Movie } from "@/types/movie";

interface UseFavoriteHandlerProps {
  movie: Movie;
  tmdbId: number;
  isValidMovie: boolean;
  favoriteVisualState: any;
  planToWatchVisualState: any;
  isImmediate: boolean;
  context: string;
  currentTab?: string;
  onToggleFavorite?: (e: React.MouseEvent) => void | Promise<void>;
  commitToggleFavorite: (movie: Movie) => Promise<void>;
  toggleFavorite: (tmdbId: number, currentTab?: string, movie?: Movie) => void | Promise<void>;
  setFavoriteDeselected: (value: boolean) => void;
}

/**
 * Hook to handle favorite button clicks
 */
export const useFavoriteHandler = ({
  movie,
  tmdbId,
  isValidMovie,
  favoriteVisualState,
  planToWatchVisualState,
  isImmediate,
  context,
  currentTab,
  onToggleFavorite,
  commitToggleFavorite,
  toggleFavorite,
  setFavoriteDeselected
}: UseFavoriteHandlerProps) => {
  
  const handleFavoriteClick = useCallback(async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isValidMovie) {
      console.error('🔘 [BUTTONS] Cannot toggle favorite: Invalid tmdbId:', {
        tmdbId,
        title: movie.title || 'Unknown'
      });
      return;
    }
    
    try {
      console.log(`🔍 [BUTTONS-FAV] Favorite button clicked for: ${movie.title}`, {
        tmdbId,
        isImmediate,
        context,
        currentMovieState: {
          favorite: favoriteVisualState.currentValue,
          planToWatch: planToWatchVisualState.currentValue
        },
        expectedChange: 'favorite only'
      });
      
      // REMOVED: setFavoriteDeselected(favoriteVisualState.currentValue);
      // This was causing the button flickering issue
      
      if (onToggleFavorite) {
        console.log(`🔍 [BUTTONS-FAV] Using custom favorite handler for: ${movie.title}`);
        await Promise.resolve(onToggleFavorite(e));
      } else if (isImmediate) {
        console.log(`🔍 [BUTTONS-FAV] Immediate commit favorite toggle for: ${movie.title} (${context})`);
        await commitToggleFavorite(movie);
      } else {
        console.log(`🔍 [BUTTONS-FAV] Collection item favorite toggle for: ${movie.title} (${context}) - should NOT affect planToWatch`);
        await Promise.resolve(toggleFavorite(tmdbId, currentTab, movie));
      }
      
      console.log(`✅ [BUTTONS-FAV] Favorite toggle completed for: ${movie.title}`);
    } catch (error) {
      console.error('❌ [BUTTONS-FAV] Error in favorite click handler:', error);
      setFavoriteDeselected(false);
    }
  }, [movie, onToggleFavorite, isImmediate, commitToggleFavorite, toggleFavorite, currentTab, favoriteVisualState.currentValue, planToWatchVisualState.currentValue, tmdbId, isValidMovie, context, setFavoriteDeselected]);

  return { handleFavoriteClick };
};
